import {FlexColumn, FlexRow, Frame, Icon, SubHeading, Text, useTableFunctions} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {useBaselineLogic} from './BaselineLogic';
import {
	BaselineItem,
	Company,
	ExpenseRow,
	EyeOptionMap,
	GroupedBaselineTotals,
	Phase,
	ProjectPhaseDeliverable,
	RoleRow,
} from './BaselineTypes';
import {TimeEstimatesTable} from './TimeEstimatesTable';
import {ExpensesEstimatesTable} from './ExpensesEstimateTable';
import {OnSaveError} from './Util';

const TableTitleWrapper = styled(FlexRow)`
	margin-bottom: 8px;
`;

const AddButtonWrapper = styled.div`
	padding-top: 8px;
	width: fit-content;
`;

const AddButton = (props: {text: string; onClick: () => void}) => (
	<AddButtonWrapper onClick={props.onClick} style={{cursor: 'pointer'}}>
		<FlexRow>
			<Icon icon="plus" size="m" color="primary"></Icon>
			<SubHeading color={'primary'}>{props.text}</SubHeading>
		</FlexRow>
	</AddButtonWrapper>
);

export const BaselineEstimateTables = ({
	setProjectBaselineItems,
	editMode,
	company,
	currency,
	useDeliverables,
	projectBudget,
	isFixedPrice,
	groupedProjectTotals,
	phase,
	phaseDeliverable,
	baselineItems,
	eyeOptionMap,
	onSaveError,
	disabledRoleIds,
	minutesPerDay,
}: {
	editMode?: boolean;
	company: Company;
	currency: string;
	setProjectBaselineItems?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	projectBudget?: number;
	isFixedPrice?: boolean;
	groupedProjectTotals?: GroupedBaselineTotals;
	phase: Phase;
	phaseDeliverable?: ProjectPhaseDeliverable;
	baselineItems: BaselineItem[];
	eyeOptionMap?: EyeOptionMap;
	useDeliverables?: boolean;
	onSaveError?: OnSaveError;
	disabledRoleIds?: string[];
	minutesPerDay?: number | null;
}) => {
	const {formatMessage} = useIntl();

	const {
		recalculateRoleRows,
		onRoleSelect,
		onRoleAdd,
		onDeleteRoleRow,
		recalculateExpenseRows,
		onExpenseCategorySelect,
		onExpenseAdd,
		onDeleteExpenseRow,
	} = useBaselineLogic({
		setProjectBaselineItems,
		projectBudget,
		isFixedPrice,
		groupedProjectTotals,
		phase,
		company,
		deliverableId: phaseDeliverable?.deliverable?.id,
	});

	const [rolesTableRef] = useTableFunctions();
	const [expensesTableRef] = useTableFunctions();

	const rolesData = baselineItems?.filter(item => item && 'phaseBaselineRoleId' in item) as RoleRow[];
	const expensesData = baselineItems?.filter(item => item && 'phaseBaselineExpenseId' in item) as ExpenseRow[];

	const hasTimeTable = rolesData?.length > 0;
	const hasExpensesTable = expensesData?.length > 0;

	const timeEstimatesSubHeading = `${formatMessage({id: 'deliverables.edit_baseline.time_estimates'})} (${
		rolesData?.length || 0
	})`;

	const expenseEstimatesSubHeading = `${formatMessage({id: 'deliverables.edit_baseline.expense_estimates'})} (${
		expensesData?.length || 0
	})`;

	return (
		<FlexColumn gap="m">
			<div style={{marginBottom: '16px'}}>
				<TableTitleWrapper>
					<Icon icon="deliverablesTimeEstimate" size="l"></Icon>
					<SubHeading size={'2'}>{timeEstimatesSubHeading}</SubHeading>
				</TableTitleWrapper>
				{hasTimeTable ? (
					<TimeEstimatesTable
						onDeleteRow={onDeleteRoleRow}
						onRoleSelect={onRoleSelect}
						recalculate={recalculateRoleRows}
						editMode={editMode}
						company={company}
						currency={currency}
						ref={rolesTableRef}
						tableData={rolesData}
						eyeOptionMap={eyeOptionMap}
						setTableData={setProjectBaselineItems}
						onSaveError={onSaveError}
						disabledRoleIds={disabledRoleIds}
						minutesPerDay={minutesPerDay}
					/>
				) : (
					<Frame centerContent={true}>
						<Text color="auxiliary">
							{useDeliverables
								? formatMessage({id: 'deliverable.no_time_estimates'})
								: formatMessage({id: 'deliverable.no_time_estimates_no_deliverables'})}
						</Text>
					</Frame>
				)}
				{editMode && (
					<AddButton text={formatMessage({id: 'deliverable.add_time_estimate'})} onClick={onRoleAdd}></AddButton>
				)}
			</div>
			<div>
				<TableTitleWrapper>
					<Icon icon="expensesNew" size="l"></Icon>
					<SubHeading size={'2'}>{expenseEstimatesSubHeading}</SubHeading>
				</TableTitleWrapper>
				{hasExpensesTable ? (
					<ExpensesEstimatesTable
						onDeleteRow={onDeleteExpenseRow}
						onCategorySelect={onExpenseCategorySelect}
						recalculateRow={recalculateExpenseRows}
						editMode={editMode}
						company={company}
						currency={currency}
						ref={expensesTableRef}
						tableData={expensesData}
						eyeOptionMap={eyeOptionMap}
						setTableData={setProjectBaselineItems}
					/>
				) : (
					<Frame centerContent={true}>
						<Text color="auxiliary">
							{useDeliverables
								? formatMessage({id: 'deliverable.no_expense_estimates'})
								: formatMessage({id: 'deliverable.no_expense_estimates_no_deliverables'})}
						</Text>
					</Frame>
				)}
				{editMode && (
					<AddButton
						text={formatMessage({id: 'deliverable.add_expenses_estimate'})}
						onClick={onExpenseAdd}
					></AddButton>
				)}
			</div>
		</FlexColumn>
	);
};
